import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../App";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Search, Power, PowerOff, X } from "lucide-react";

const StatusPill = ({ status }) => {
  const map = {
    active: "bg-[#EAF3EC] text-[#4A6D50] border-[#CDE0D3]",
    inactive: "bg-[#F8F6F2] text-[#6F6B66] border-[#ECE7DF]",
    past_due: "bg-[#FCECEA] text-[#8A3B2E] border-[#F0C7C0]",
    canceled: "bg-[#F8F6F2] text-[#6F6B66] border-[#ECE7DF]",
  };
  return (
    <span className={`inline-block rounded-full border px-2.5 py-0.5 text-[10px] kt-tracking uppercase ${map[status] || map.inactive}`}>
      {status || "inactive"}
    </span>
  );
};

export default function AdminSalons() {
  const [items, setItems] = useState([]);
  const [overview, setOverview] = useState(null);
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [settings, setSettings] = useState(null);

  const load = async () => {
    const [list, ov, ht] = await Promise.all([
      axios.get(`${API}/admin/salons`),
      axios.get(`${API}/admin/salons/stats/overview`),
      axios.get(`${API}/admin/hairtu/settings`),
    ]);
    setItems(list.data.items);
    setOverview(ov.data);
    setSettings(ht.data);
  };

  useEffect(() => { load(); }, []);

  const filtered = items.filter((s) => {
    if (!q) return true;
    const t = q.toLowerCase();
    return (s.salon_name || "").toLowerCase().includes(t) ||
      (s.email || "").toLowerCase().includes(t) ||
      (s.country || "").toLowerCase().includes(t);
  });

  const openDetail = async (salon_id) => {
    const r = await axios.get(`${API}/admin/salons/${salon_id}`);
    setDetail(r.data);
  };

  const toggleActive = async (salon, activate) => {
    setBusy(true);
    try {
      await axios.patch(`${API}/admin/salons/${salon.salon_id}`, { admin_active: activate });
      toast.success(activate ? "Salon reactivated" : "Salon suspended");
      await load();
      if (detail?.salon.salon_id === salon.salon_id) await openDetail(salon.salon_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setBusy(false); }
  };

  const saveSettings = async () => {
    setBusy(true);
    try {
      const payload = {
        price_eur: parseFloat(settings.price_eur),
        vat_included: !!settings.vat_included,
        banner_en: settings.banner_en || "",
        banner_it: settings.banner_it || "",
        banner_active: !!settings.banner_active,
        referral_param: settings.referral_param || "hairtu_salon",
      };
      await axios.patch(`${API}/admin/hairtu/settings`, payload);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-8" data-testid="admin-salons">
      <div>
        <h1 className="font-serif text-3xl text-[#383633] tracking-tight">Hairtu Professional</h1>
        <p className="text-[#6F6B66] mt-1 text-sm">Salon accounts, subscriptions, banners and settings.</p>
      </div>

      {overview && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <OverviewCard title="Total salons" value={overview.salons_total} />
          <OverviewCard title="Active" value={overview.salons_active} />
          <OverviewCard title="Past due" value={overview.salons_past_due} />
          <OverviewCard title="Suspended" value={overview.salons_suspended} />
          <OverviewCard title="Canceled" value={overview.salons_canceled} />
          <OverviewCard title="Assessments total" value={overview.assessments_total} />
          <OverviewCard title="This month" value={overview.assessments_this_month} />
        </div>
      )}

      {/* Settings panel */}
      {settings && (
        <div className="rounded-2xl bg-white border kt-line p-6" data-testid="admin-hairtu-settings">
          <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-4">Plan settings & banner</div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs kt-tracking uppercase text-[#6F6B66]">Monthly price (EUR)</Label>
              <Input type="number" step="0.01" value={settings.price_eur}
                data-testid="setting-price"
                onChange={(e) => setSettings({ ...settings, price_eur: e.target.value })}
                className="mt-2 border-[#ECE7DF] rounded-xl h-11" />
              <div className="text-[11px] text-[#6F6B66] mt-1">Display only — Stripe recurring price is fixed at €30/mo via catalog.</div>
            </div>
            <div>
              <Label className="text-xs kt-tracking uppercase text-[#6F6B66]">Referral parameter</Label>
              <Input value={settings.referral_param} data-testid="setting-referral"
                onChange={(e) => setSettings({ ...settings, referral_param: e.target.value })}
                className="mt-2 border-[#ECE7DF] rounded-xl h-11" />
            </div>
            <label className="flex items-center gap-3 sm:col-span-2">
              <input type="checkbox" checked={settings.vat_included}
                data-testid="setting-vat"
                onChange={(e) => setSettings({ ...settings, vat_included: e.target.checked })}
                className="h-4 w-4" />
              <span className="text-sm text-[#383633]">VAT included in displayed price</span>
            </label>
            <div className="sm:col-span-2 border-t kt-line pt-4">
              <label className="flex items-center gap-3 mb-3">
                <input type="checkbox" checked={settings.banner_active}
                  data-testid="setting-banner-active"
                  onChange={(e) => setSettings({ ...settings, banner_active: e.target.checked })}
                  className="h-4 w-4" />
                <span className="text-sm text-[#383633]">Promotional banner active</span>
              </label>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs kt-tracking uppercase text-[#6F6B66]">Banner (English)</Label>
                  <Input value={settings.banner_en || ""} data-testid="setting-banner-en"
                    onChange={(e) => setSettings({ ...settings, banner_en: e.target.value })}
                    className="mt-2 border-[#ECE7DF] rounded-xl h-11" />
                </div>
                <div>
                  <Label className="text-xs kt-tracking uppercase text-[#6F6B66]">Banner (Italian)</Label>
                  <Input value={settings.banner_it || ""} data-testid="setting-banner-it"
                    onChange={(e) => setSettings({ ...settings, banner_it: e.target.value })}
                    className="mt-2 border-[#ECE7DF] rounded-xl h-11" />
                </div>
              </div>
            </div>
          </div>
          <div className="mt-6 flex justify-end">
            <Button onClick={saveSettings} disabled={busy} data-testid="save-hairtu-settings"
              className="rounded-full bg-[#383633] hover:bg-[#4A4238] text-white h-11 px-6 text-xs kt-tracking uppercase">
              Save settings
            </Button>
          </div>
        </div>
      )}

      {/* Salons list */}
      <div className="rounded-2xl bg-white border kt-line p-6" data-testid="admin-salons-list">
        <div className="flex items-center justify-between mb-4 gap-3">
          <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66]">Salon accounts</div>
          <div className="relative">
            <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[#6F6B66]" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…"
              data-testid="search-salons"
              className="pl-8 h-9 rounded-full border-[#ECE7DF] w-56" />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] kt-tracking uppercase text-[#6F6B66] border-b kt-line">
                <th className="text-left py-2 font-normal">Salon</th>
                <th className="text-left py-2 font-normal">Contact</th>
                <th className="text-left py-2 font-normal">Country</th>
                <th className="text-left py-2 font-normal">Subscription</th>
                <th className="text-left py-2 font-normal">Admin</th>
                <th className="text-right py-2 font-normal">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => (
                <tr key={s.salon_id} className="border-b kt-line hover:bg-[#FAF8F4]" data-testid={`salon-row-${s.salon_id}`}>
                  <td className="py-3">
                    <button onClick={() => openDetail(s.salon_id)} className="text-[#383633] hover:underline">
                      {s.salon_name}
                    </button>
                    <div className="text-[11px] text-[#6F6B66]">{s.email}</div>
                  </td>
                  <td className="py-3 text-[#6F6B66]">{s.contact_name}</td>
                  <td className="py-3 text-[#6F6B66]">{s.country}</td>
                  <td className="py-3"><StatusPill status={s.subscription_status} /></td>
                  <td className="py-3">
                    {s.admin_active
                      ? <span className="text-xs text-[#4A6D50]">Enabled</span>
                      : <span className="text-xs text-[#8A3B2E]">Suspended</span>}
                  </td>
                  <td className="py-3 text-right">
                    {s.admin_active ? (
                      <button onClick={() => toggleActive(s, false)} disabled={busy}
                        data-testid={`suspend-${s.salon_id}`}
                        className="inline-flex items-center gap-1 text-xs text-[#8A3B2E] hover:underline">
                        <PowerOff className="h-3 w-3" /> Suspend
                      </button>
                    ) : (
                      <button onClick={() => toggleActive(s, true)} disabled={busy}
                        data-testid={`activate-${s.salon_id}`}
                        className="inline-flex items-center gap-1 text-xs text-[#4A6D50] hover:underline">
                        <Power className="h-3 w-3" /> Reactivate
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="py-8 text-center text-[#6F6B66]">No salons yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail modal */}
      {detail && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={() => setDetail(null)}>
          <div className="bg-white max-w-2xl w-full max-h-[85vh] overflow-auto rounded-2xl border kt-line p-6" onClick={(e) => e.stopPropagation()} data-testid="salon-detail">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66]">Salon</div>
                <h2 className="font-serif text-2xl text-[#383633]">{detail.salon.salon_name}</h2>
                <div className="text-sm text-[#6F6B66] mt-1">{detail.salon.email}</div>
              </div>
              <button onClick={() => setDetail(null)} className="text-[#6F6B66] hover:text-[#383633]">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="mt-6 grid sm:grid-cols-2 gap-3 text-sm">
              <Info label="Contact" value={detail.salon.contact_name} />
              <Info label="Country" value={detail.salon.country} />
              <Info label="VAT" value={detail.salon.vat_number || "—"} />
              <Info label="Slug" value={detail.salon.slug} />
              <Info label="Subscription" value={detail.salon.subscription_status} />
              <Info label="Period end" value={detail.salon.current_period_end ? new Date(detail.salon.current_period_end).toLocaleString() : "—"} />
              <Info label="Assessments (total)" value={detail.stats.total_assessments} />
              <Info label="This month" value={detail.stats.assessments_this_month} />
              <Info label="Woman path" value={detail.stats.women_path} />
              <Info label="Man path" value={detail.stats.men_path} />
            </div>
            <div className="mt-6">
              <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-2">Audit log (last 50)</div>
              <div className="max-h-56 overflow-auto border kt-line rounded-xl">
                <table className="w-full text-xs">
                  <tbody>
                    {detail.audit_log.length === 0 && (
                      <tr><td className="p-3 text-center text-[#6F6B66]">No audit events yet.</td></tr>
                    )}
                    {detail.audit_log.map((a, i) => (
                      <tr key={i} className="border-b kt-line last:border-0">
                        <td className="p-2 text-[#383633] font-mono">{a.kind}</td>
                        <td className="p-2 text-[#6F6B66]">{a.status || a.subscription_id || ""}</td>
                        <td className="p-2 text-[#6F6B66] text-right">{new Date(a.at).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function OverviewCard({ title, value }) {
  return (
    <div className="rounded-2xl bg-white border kt-line p-4">
      <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66]">{title}</div>
      <div className="font-serif text-2xl text-[#383633] mt-1">{value}</div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66]">{label}</div>
      <div className="text-[#383633]">{value}</div>
    </div>
  );
}
